import React from "react";
import AddressDisplay from "../../../../../components/ui/display/AddressDisplay";
import ContentCard from "../../../../../components/ui/cards/ContentCard";
import { usaStates, filingStatuses } from "../../../../../components/ui/forms/selectOptionData";

// Helper function to convert state code to full state name
const getStateName = (stateCode) => {
  if (!stateCode) return stateCode;
  const stateCodeStr = typeof stateCode === 'string' ? stateCode : String(stateCode);
  const state = usaStates().find((s) => s.value === stateCodeStr.toLowerCase());
  return state ? state.label : stateCodeStr;
};

// Helper to get filing status label
const getFilingStatusLabel = (value) => {
  const status = filingStatuses().find((s) => s.value === value);
  return status ? status.label : value;
};

// Helper component to render a row with label and value
const InfoRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="info-row">
      <span className="info-row-label">{label}:</span>
      <span className="info-row-value">{value}</span>
    </div>
  );
};

const PTFBackgroundSection = ({ background, situation }) => {
  if (!background && !situation) return null;

  const hasPersonalInfo = background?.firstname || background?.lastname || 
                          background?.email || background?.phone || 
                          background?.dBirth || background?.occupation;
  const hasAddresses = background?.addresses && background.addresses.length > 0;
  const hasFilingStatus = situation?.taxYears || situation?.filingStatus;

  if (!hasPersonalInfo && !hasAddresses && !hasFilingStatus) return null;

  // Format filing status data
  const formattedTaxYears = Array.isArray(situation?.taxYears) 
    ? situation.taxYears.join(", ") 
    : situation?.taxYears || null;
  const formattedFilingStatus = situation?.filingStatus 
    ? getFilingStatusLabel(situation.filingStatus) 
    : null;
  
  // Format income states
  const formattedIncomeStates = (() => {
    const incomeStates = situation?.incomeStates;
    if (!incomeStates) return null;
    if (typeof incomeStates === 'object' && !Array.isArray(incomeStates)) {
      // Object format: { "2024": ["NY", "PA"], "2023": ["CA"] }
      const states = [...new Set(Object.values(incomeStates).flat().filter(Boolean))];
      return states.length > 0 ? states.map(s => s.toUpperCase()).join(", ") : null;
    }
    if (Array.isArray(incomeStates) && incomeStates.length > 0) {
      return incomeStates.map(s => s.toUpperCase()).join(", ");
    }
    return null;
  })();

  return (
    <>
      {/* Filing Basics Card */}
      {hasFilingStatus && (
        <ContentCard style={{ borderRadius: "0" }}>
          <h4 className="content-card-title">Filing Basics</h4>
          <div className="content-card-rows">
            <InfoRow label="Filing Year(s)" value={formattedTaxYears} />
            <InfoRow label="Status" value={formattedFilingStatus} />
            <InfoRow label="Income States" value={formattedIncomeStates} />
          </div>
        </ContentCard>
      )}

      {/* Personal Information Card */}
      {hasPersonalInfo && (
        <ContentCard style={{ borderRadius: "0" }}>
          <h4 className="content-card-title">Personal Information</h4>
          <div className="content-card-rows">
            <InfoRow label="First Name" value={background?.firstname} />
            <InfoRow label="Last Name" value={background?.lastname} />
            <InfoRow label="Email" value={background?.email} />
            <InfoRow label="Phone" value={background?.phone} />
            <InfoRow label="Date of Birth" value={background?.dBirth} />
            <InfoRow label="Occupation" value={background?.occupation} />
          </div>
        </ContentCard>
      )}

      {/* Addresses Card */}
      {hasAddresses && (
        <ContentCard style={{ borderRadius: "0" }}>
          <h4 className="content-card-title">Addresses</h4>
          <div className="address-display-container">
            {background.addresses.map((addr, index) => (
              <AddressDisplay
                key={index}
                address={{
                  ...addr,
                  stateAddress: getStateName(addr.stateAddress),
                }}
              />
            ))}
          </div>
        </ContentCard>
      )}
    </>
  );
};

export default PTFBackgroundSection;

